import * as React from "react";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import LabeledPlay from "./LabeledPlay";
import ArrayUtils from "./util/array";
/**
 * Shared trick decomposition: turns the raw `Trick` into, per player, the
 * grouped cards. Both the flat previous-trick list and the in-table compass
 * layout build on this so the card-coalescing logic lives in exactly one place.
 */
var decomposeTrick = function (trick) {
    var _a;
    var blankCards = trick.played_cards.length > 0
        ? Array(trick.played_cards[0].cards.length).fill("🂠")
        : ["🂠"];
    var betterPlayer = trick.played_cards.length > 0
        ? ((_a = trick.played_cards[0].better_player) !== null && _a !== void 0 ? _a : null)
        : null;
    var playedByID = {};
    var cardsFromMappingByID = {};
    trick.played_cards.forEach(function (played, idx) {
        playedByID[played.id] = played;
        var m = trick.played_card_mappings
            ? trick.played_card_mappings[idx]
            : undefined;
        if (m !== undefined && m !== null && m.length > 0) {
            // We should coalesce blocks of `Repeated` of count 1 together, since
            // that displays more nicely.
            var mapping_1 = [];
            var singles_1 = [];
            m.forEach(function (mm) {
                if ("Repeated" in mm && mm.Repeated.count === 1) {
                    singles_1.push(mm.Repeated.card.card);
                }
                else if ("Repeated" in mm) {
                    mapping_1.push(ArrayUtils.range(mm.Repeated.count, function (_) { return mm.Repeated.card.card; }));
                }
                else if ("Tractor" in mm) {
                    mapping_1.push(mm.Tractor.members.flatMap(function (mmm) {
                        return ArrayUtils.range(mm.Tractor.count, function (_) { return mmm.card; });
                    }));
                }
            });
            mapping_1.push(singles_1);
            cardsFromMappingByID[played.id] = mapping_1;
        }
    });
    return { playedByID: playedByID, cardsFromMappingByID: cardsFromMappingByID, betterPlayer: betterPlayer, blankCards: blankCards };
};
/**
 * Measure a play's footprint. `cols` = the most cards shown side-by-side in any
 * row; `rows` = stacked rows (grouped runs render left-to-right as one row; a
 * `more`/overflow strip adds a row). It only needs to be big enough that the
 * uniform slot never clips a play.
 */
var footprintOf = function (cards, groupedCards, moreCards) {
    var cols;
    var rows = 1;
    if (groupedCards !== undefined && groupedCards.length > 0) {
        // Grouped runs render left-to-right on one row, so total width is the sum.
        cols = groupedCards.reduce(function (acc, g) { return acc + g.length; }, 0);
    }
    else {
        cols = cards.length;
    }
    cols = Math.max(cols, 1);
    if (moreCards !== undefined && moreCards.length > 0) {
        rows += 1;
        cols = Math.max(cols, moreCards.length);
    }
    return { cols: cols, rows: rows };
};
/** The little "(!)" winner / "(-)" better-player marker shown beside a name. */
export var winnerSuffix = function (winning, better) {
    if (winning) {
        return (React.createElement(React.Fragment, null,
            " ",
            React.createElement(Tooltip, { id: "winningTip", place: "bottom" }),
            React.createElement("span", { "data-tooltip-id": "winningTip", "data-tooltip-content": "Current winner of trick" },
                "(",
                React.createElement("code", null, "!"),
                ")")));
    }
    if (better) {
        return (React.createElement(React.Fragment, null,
            " ",
            React.createElement(Tooltip, { id: "betterTip", place: "bottom" }),
            React.createElement("span", { "data-tooltip-id": "betterTip", "data-tooltip-content": "First player who can prevent the attempted throw" },
                "(",
                React.createElement("code", null, "-"),
                ")")));
    }
    return React.createElement(React.Fragment, null);
};
/**
 * Build, for every player that has played this trick, the rendered play to drop
 * into their compass seat, plus the uniform footprint that all four slots should
 * adopt (= the largest play in the trick). The name label is intentionally left
 * OUT of the returned node — the table renders the team-colored name pill beside
 * each seat, so the play itself is just the cards.
 */
export var buildSeatPlays = function (props) {
    var _a = decomposeTrick(props.trick), cardsFromMappingByID = _a.cardsFromMappingByID, betterPlayer = _a.betterPlayer;
    var plays = {};
    var uniform = { cols: 1, rows: 1 };
    props.trick.played_cards.forEach(function (played) {
        var _a;
        var id = played.id;
        var winning = props.trick.current_winner === id;
        var better = betterPlayer === id;
        var cards = played.cards;
        var groupedCards = cardsFromMappingByID[id];
        var moreCards = played.bad_throw_cards;
        var footprint = footprintOf(cards, groupedCards, moreCards);
        uniform = {
            cols: Math.max(uniform.cols, footprint.cols),
            rows: Math.max(uniform.rows, footprint.rows),
        };
        var className = classNames(winning ? "winning" : props.trick.player_queue[0] === id ? "notify" : "", {
            landlord: id === props.landlord || ((_a = props.landlords_team) === null || _a === void 0 ? void 0 : _a.includes(id)),
        });
        plays[id] = {
            winning: winning,
            better: better,
            node: (React.createElement(LabeledPlay, { id: id, label: null, className: className, groupedCards: groupedCards, cards: cards, trump: props.trick.trump, next: props.next, moreCards: moreCards })),
        };
    });
    return { plays: plays, uniform: uniform };
};
var TrickE = function (props) {
    var _a;
    var namesById = ArrayUtils.mapObject(props.players, function (p) { return [
        String(p.id),
        p.name,
    ]; });
    var _b = decomposeTrick(props.trick), playedByID = _b.playedByID, cardsFromMappingByID = _b.cardsFromMappingByID, betterPlayer = _b.betterPlayer, blankCards = _b.blankCards;
    var playOrder = [];
    if (props.showTrickInPlayerOrder) {
        playOrder = props.players.map(function (p) { return p.id; });
    }
    else {
        props.trick.played_cards.forEach(function (played) { return playOrder.push(played.id); });
        props.trick.player_queue.forEach(function (id) { return playOrder.push(id); });
    }
    var isRainbow = ((_a = props.trick.trick_format) === null || _a === void 0 ? void 0 : _a.is_rainbow) === true;
    return (React.createElement("div", { className: "trick" },
        isRainbow && (React.createElement("div", { style: { fontWeight: "bold", marginBottom: "4px" } }, "\uD83C\uDF08 Rainbow trick \u2014 play same rank across \u22654 suits to counter")),
        playOrder.map(function (id) {
            var _a, _b;
            var winning = props.trick.current_winner === id;
            var better = betterPlayer === id;
            var cards = id in playedByID ? playedByID[id].cards : blankCards;
            var suffix = winnerSuffix(winning, better);
            var className = classNames(winning
                ? "winning"
                : props.trick.player_queue[0] === id
                    ? "notify"
                    : "", {
                landlord: id === props.landlord || ((_a = props.landlords_team) === null || _a === void 0 ? void 0 : _a.includes(id)),
            });
            return (React.createElement(LabeledPlay, { key: id, id: id, label: React.createElement(React.Fragment, null,
                    namesById[id] +
                        (id === props.landlord ? " " + props.landlord_suffix : ""),
                    suffix), className: className, groupedCards: cardsFromMappingByID[id], cards: cards, trump: props.trick.trump, next: props.next, moreCards: (_b = playedByID[id]) === null || _b === void 0 ? void 0 : _b.bad_throw_cards }));
        })));
};
export default TrickE;
